import { Box, Skeleton, Theme, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useMatch } from 'react-router-dom';
import { Routes } from '../routes/routes';
import { grey } from '@mercedes-benz/mui5-theme';
import { WbAspectRatio, WbGrid, WbGridCol, WbGridRow, WbHeading } from '@workbench/react';
import { useLoadingContext } from './contexts/LoadingContext';

const useStyles = makeStyles<{ isAdminView: boolean }>()((theme: Theme, { isAdminView }) => {
    return {
        skeletonBackground: {
            backgroundColor: isAdminView ? theme.palette.common.black : theme.palette.common.white,
            height: '100%',
        },

        skeleton: {
            backgroundColor: isAdminView ? grey[30] : grey[85],
        },
        content: {
            marginTop: theme.spacing(5),
        },
        spacing: {
            marginBottom: theme.spacing(5),
        },
        tableSpacing: {
            marginTop: theme.spacing(10),
        },
    };
});

interface LoadingSkeletonProps {
    isTable?: boolean;
    showSkeletonImmediately?: boolean;
}
export function LoadingSkeleton(props: Readonly<LoadingSkeletonProps>) {
    const isAdminPage = useMatch(`/${Routes.admin}/*`);
    const isMasterDataPage = useMatch(`/${Routes.admin}/*`);
    const isExactEditPage = isMasterDataPage ? isMasterDataPage?.pathname.split('/').length > 4 : false;
    const { loadData } = useLoadingContext();
    const theme = useTheme();
    const { classes } = useStyles({ isAdminView: !isAdminPage });

    const userView = (
        <div className={classes.skeletonBackground}>
            <WbGrid full-width>
                <WbAspectRatio ratio="4x1">
                    <Skeleton className={classes.skeleton} variant="rectangular" />
                </WbAspectRatio>

                <div className={classes.content}>
                    <WbGrid>
                        <WbGridRow>
                            <WbGridCol mq1="8">
                                <>
                                    <WbHeading size="l">
                                        <Skeleton
                                            className={classes.skeleton}
                                            variant="text"
                                            width={theme.spacing(100)}
                                            height={theme.spacing(8)}
                                        />
                                    </WbHeading>
                                    <WbHeading size="m" className={classes.spacing}>
                                        <Skeleton
                                            className={classes.skeleton}
                                            variant="text"
                                            width={theme.spacing(80)}
                                            height={theme.spacing(8)}
                                        />
                                    </WbHeading>
                                </>
                            </WbGridCol>
                            <WbGridCol mq1="8">
                                <Box mt={2}>
                                    <Skeleton
                                        className={classes.skeleton}
                                        variant="rectangular"
                                        width={theme.spacing(24)}
                                        height={theme.spacing(5)}
                                    />
                                </Box>
                            </WbGridCol>
                        </WbGridRow>
                    </WbGrid>
                </div>
            </WbGrid>
        </div>
    );
    const adminView = (
        <div className={classes.skeletonBackground}>
            <WbGrid>
                <WbGrid>
                    <Skeleton
                        className={classes.skeleton}
                        variant="text"
                        width={theme.spacing(40)}
                        height={theme.spacing(8)}
                    />
                </WbGrid>
                <WbGrid>
                    <Skeleton
                        className={classes.skeleton}
                        variant="text"
                        width={theme.spacing(45)}
                        height={theme.spacing(8)}
                    />
                </WbGrid>
                <WbGrid className={classes.tableSpacing}>
                    <Skeleton
                        className={classes.skeleton}
                        variant="rectangular"
                        width="70vw"
                        height={theme.spacing(50)}
                    />
                </WbGrid>
            </WbGrid>
        </div>
    );

    const table = (
        <div className={classes.skeletonBackground}>
            <WbGrid className={classes.tableSpacing}>
                <Skeleton className={classes.skeleton} variant="rectangular" width="70vw" height={theme.spacing(50)} />
            </WbGrid>
        </div>
    );

    if (isExactEditPage) {
        return <div className={classes.skeletonBackground} />;
    }
    if (props.isTable) {
        return table;
    }

    if (!loadData && !props.showSkeletonImmediately) {
        return <></>;
    }
    return isAdminPage ? adminView : userView;
}

export default React.memo(LoadingSkeleton);
